import type { OrderStatus } from '../../../types'
import { cx, focus } from '../../../lib/ui-primitives'

// ============================================
// FILTER BAR
// Order status filter buttons
// ============================================

export type FilterType = 'all' | OrderStatus

export interface FilterBarProps {
  filter: FilterType
  onFilterChange: (filter: FilterType) => void
  pendingCount?: number
}

const filters: FilterType[] = ['all', 'pending', 'approved', 'completed', 'rejected']

/**
 * FilterBar - Order status filter buttons
 *
 * Horizontal scrollable filter bar with active state.
 */
export function FilterBar({ filter, onFilterChange, pendingCount = 0 }: FilterBarProps) {
  return (
    <div
      className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-thin"
      role="tablist"
      aria-label="Filter orders by status"
    >
      {filters.map((f) => (
        <FilterButton
          key={f}
          label={f}
          active={filter === f}
          onClick={() => onFilterChange(f)}
          badge={f === 'pending' && pendingCount > 0 ? pendingCount : undefined}
        />
      ))}
    </div>
  )
}

interface FilterButtonProps {
  label: string
  active: boolean
  onClick: () => void
  badge?: number
}

function FilterButton({ label, active, onClick, badge }: FilterButtonProps) {
  return (
    <button
      onClick={onClick}
      role="tab"
      aria-selected={active}
      className={cx(
        'px-3 sm:px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors',
        focus.ring,
        active
          ? 'bg-accent-primary text-white'
          : 'bg-white/5 text-content-secondary hover:bg-white/10 border border-white/10'
      )}
    >
      {label.charAt(0).toUpperCase() + label.slice(1)}
      {badge !== undefined && (
        <span className="ml-2 px-1.5 py-0.5 bg-amber-500 text-black text-xs rounded-full font-semibold">
          {badge}
        </span>
      )}
    </button>
  )
}
